import React, { useState, useEffect } from "react";
import { Box, Typography, styled } from "@mui/material";
import ProfileFormSteps from "../../ui/ProfileFormSteps.tsx";

interface ProfileData {
  profileName: string;
  collections: string[];
  includeHistory: boolean;
  systemInstruction: string;
  modelId: string;
  numSearchDocuments: number;
  numChatMessages: number;
  temperature: number;
}

interface ProfileFormWizardProps {
  activeStep: number;
  profileData: ProfileData;
  availableCollections: string[];
  isLoading: boolean;
  isDarkMode: boolean;
  adjustmentsExpanded: boolean;
  setAdjustmentsExpanded: (expanded: boolean) => void;
  instructionsExpanded: boolean;
  setInstructionsExpanded: (expanded: boolean) => void;
  handleCollectionToggle: (collection: string) => void;
  setProfileData: React.Dispatch<React.SetStateAction<ProfileData>>;
  isDefaultCollection: (collection: string) => boolean;
  getUniqueCollections: () => string[];
  isEditMode: boolean;
  error?: string;
  onClearError?: () => void;
  onNext?: () => void;
  canGoNext?: boolean;
  nextButtonText?: string;
}

const SectionTitle = styled(Typography)<{ $isDark?: boolean }>(({ $isDark }) => ({
  fontSize: "18px",
  fontWeight: "bold",
  color: $isDark ? "#FFFFFF" : "#1F2937",
  marginBottom: "16px",
  marginTop: "8px",
  "@media (max-width: 600px)": {
    fontSize: "16px",
  },
}));

const ProfileFormWizard = ({
  activeStep,
  profileData,
  availableCollections,
  isLoading,
  isDarkMode,
  adjustmentsExpanded,
  setAdjustmentsExpanded,
  instructionsExpanded,
  setInstructionsExpanded,
  handleCollectionToggle,
  setProfileData,
  isDefaultCollection,
  getUniqueCollections,
  isEditMode,
  error,
  onClearError,
  onNext,
  canGoNext,
  nextButtonText
}: ProfileFormWizardProps) => {

  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const handleProfileDataChange: React.Dispatch<React.SetStateAction<ProfileData>> = (value) => {
    if (onClearError && error) {
      onClearError();
    }
    setProfileData(value);
  };

  return (
    <Box>
      <SectionTitle $isDark={isDarkMode}>
        {textConfig.PROFILE_FORM_WIZARD_TITLE}
      </SectionTitle>
      
      <ProfileFormSteps
        activeStep={activeStep}
        showPreview={false}
        profileData={profileData}
        availableCollections={availableCollections}
        isLoading={isLoading}
        isDarkMode={isDarkMode}
        adjustmentsExpanded={adjustmentsExpanded}
        setAdjustmentsExpanded={setAdjustmentsExpanded}
        instructionsExpanded={instructionsExpanded}
        setInstructionsExpanded={setInstructionsExpanded}
        handleCollectionToggle={handleCollectionToggle}
        setProfileData={handleProfileDataChange}
        isDefaultCollection={isDefaultCollection}
        getUniqueCollections={getUniqueCollections}
        isEditMode={isEditMode}
        onNext={onNext}
        canGoNext={canGoNext}
        nextButtonText={nextButtonText}
      />
    </Box>
  );
};

export default ProfileFormWizard;